import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import BannerSection from '../components/BannerSection';
import BookingForm from '../components/BookingForm';
import TestimonialSlider from '../components/TestimonialSlider';
import CounterSection from '../components/CounterSection';
import VideoSection from '../components/VideoSection';
import { useCart } from '../context/CartContext';
import flatpickr from 'flatpickr';
import { WOW } from 'wowjs';
import 'animate.css';

/* ─── Rooms ──────────────────────────────────────────── */
const rooms = [
  { id: 'r1', img: '/images/rooms/room-service1-1.jpg', title: 'Standard Room',     price: 30000,  currency: '₦', persons: '1–2', bed: 'King Size Bed',  bath: 'En-Suite',  sqft: '280 sq ft', type: 'room', flag: '🇨🇳', flagLabel: 'China Rate' },
  { id: 'r2', img: '/images/rooms/room-service1-2.jpg', title: 'Deluxe Room',        price: 35000,  currency: '₦', persons: '1–2', bed: 'King Size Bed',  bath: 'En-Suite',  sqft: '340 sq ft', type: 'room', flag: '🇨🇦', flagLabel: 'Canada Rate' },
  { id: 'r3', img: '/images/rooms/room-service1-3.jpg', title: 'Executive Room',     price: 40000,  currency: '₦', persons: '1–2', bed: 'King Size Bed',  bath: 'En-Suite',  sqft: '420 sq ft', type: 'room', flag: '🇬🇧', flagLabel: 'UK Rate' },
  { id: 'r4', img: '/images/rooms/room-service1-4.jpg', title: 'Suite',              price: 70000,  currency: '₦', persons: '1–4', bed: 'King Size Bed',  bath: 'Jacuzzi',   sqft: '680 sq ft', type: 'room', flag: '🇺🇸', flagLabel: 'USA Rate' },
  { id: 'r5', img: '/images/rooms/room-service1-5.jpg', title: 'Classic Room',       price: 30000,  currency: '₦', persons: '1–2', bed: 'Double Bed',    bath: 'En-Suite',  sqft: '260 sq ft', type: 'room', flag: '🇨🇳', flagLabel: 'China Rate' },
  { id: 'r6', img: '/images/rooms/room-service1-6.jpg', title: 'Classic Room Plus',  price: 35000,  currency: '₦', persons: '1–2', bed: 'Double Bed',    bath: 'En-Suite',  sqft: '300 sq ft', type: 'room', flag: '🇨🇦', flagLabel: 'Canada Rate' },
  { id: 'r7', img: '/images/rooms/room-service1-7.jpg', title: 'Lounge Suite',       price: 40000,  currency: '₦', persons: '1–3', bed: 'King Size Bed',  bath: 'En-Suite',  sqft: '500 sq ft', type: 'room', flag: '🇬🇧', flagLabel: 'UK Rate' },
];

const offers = [
  { img: '/images/resource/service1-1.jpg', title: 'Outdoor Terrace & Garden', desc: 'Relax under our shaded terrace with colourful seating on a lush green lawn — perfect for social gatherings.' },
  { img: '/images/facilities/games-room.jpg', title: 'Games Room & Bar Lounge', desc: 'Enjoy pool tables, wicker lounge seating, and a fully stocked premium bar in our stylish games room.' },
  { img: '/images/resource/service1-3.jpg', title: 'Ample Secure Parking', desc: 'Our spacious paved compound offers free secure parking for all guests throughout their stay.' },
];

const menuItems = [
  { img: '/images/food/food-vegetable-soup.jpg', title: 'Vegetable Soup',         price: '₦3,500', badge: 'Local' },
  { img: '/images/food/food-pepper-stir-fry.jpg', title: 'Pepper Stir-Fry',       price: '₦4,200', badge: 'Chef Special' },
  { img: '/images/food/food-vegetable-soup.jpg', title: 'Ofe Onugbu',             price: '₦3,800', badge: 'Traditional' },
  { img: '/images/food/food-pepper-stir-fry.jpg', title: 'Fisherman Stew',        price: '₦5,000', badge: 'Seafood' },
  { img: '/images/food/food-vegetable-soup.jpg', title: 'Egusi Soup',             price: '₦3,500', badge: 'Local' },
  { img: '/images/food/food-pepper-stir-fry.jpg', title: 'Nkwobi & Isi-Ewu',     price: '₦6,000', badge: 'Delicacy' },
  { img: '/images/food/food-vegetable-soup.jpg', title: 'Grilled Catfish',        price: '₦5,500', badge: 'Grilled' },
  { img: '/images/food/food-pepper-stir-fry.jpg', title: 'Jollof Rice & Chicken', price: '₦2,800', badge: 'Popular' },
];

/* ─── Room Card ─────────────────────────────────────── */
function RoomCard({ room }) {
  const { addToCart, cartItems } = useCart();
  const [added, setAdded] = useState(false);
  const inCart = cartItems.some(i => i.id === room.id && i.type === 'room');

  const handleAdd = () => {
    addToCart({ id: room.id, title: room.title, price: room.price, img: room.img, type: 'room', currency: room.currency });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="room-card">
      <div className="room-card-img">
        <Link to="/room-details"><img src={room.img} alt={room.title} /></Link>
        <span className="room-price">{room.currency}{room.price.toLocaleString()}<small>/night</small></span>
        <span className="room-flag-tag">{room.flag} {room.flagLabel}</span>
      </div>
      <div className="room-card-body">
        <h4 className="room-card-title"><Link to="/room-details">{room.title}</Link></h4>
        <ul className="room-facilities">
          <li><i className="fas fa-user"></i> {room.persons} Persons</li>
          <li><i className="fas fa-bed"></i> {room.bed}</li>
          <li><i className="fas fa-bath"></i> {room.bath}</li>
          <li><i className="fas fa-wifi"></i> Free WiFi</li>
        </ul>
        <div className="room-sqft"><i className="fas fa-ruler-combined me-1"></i>{room.sqft}</div>
        <div className="room-card-actions">
          <Link to="/room-details" className="btn-details">View Details</Link>
          <button className={`btn-book${inCart ? ' in-cart' : ''}${added ? ' just-added' : ''}`} onClick={handleAdd}>
            {added ? <><i className="fas fa-check"></i> Added!</> : inCart ? <><i className="fas fa-cart-shopping"></i> In Cart</> : <><i className="fas fa-cart-plus"></i> Book Room</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Contact Form ───────────────────────────────────── */
function ContactForm() {
  const [form, setForm]         = useState({ name: '', email: '', checkIn: '', checkOut: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]   = useState(false);
  const checkInRef  = useRef(null);
  const checkOutRef = useRef(null);

  useEffect(() => {
    const fpIn  = flatpickr(checkInRef.current,  { dateFormat: 'Y-m-d', minDate: 'today' });
    const fpOut = flatpickr(checkOutRef.current, { dateFormat: 'Y-m-d', minDate: 'today' });
    return () => { fpIn.destroy(); fpOut.destroy(); };
  }, []);

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = e => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setForm({ name: '', email: '', checkIn: '', checkOut: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    }, 900);
  };

  return (
    <form onSubmit={submit} className="contact-form-inner">
      {submitted && <div className="form-success"><i className="fas fa-circle-check"></i> Thank you! We'll be in touch soon.</div>}
      <div className="row">
        <div className="form-group col-lg-6 col-md-6">
          <input type="text" name="name" value={form.name} onChange={handle} placeholder="Your Full Name" required />
        </div>
        <div className="form-group col-lg-6 col-md-6">
          <input type="email" name="email" value={form.email} onChange={handle} placeholder="Email Address" required />
        </div>
        <div className="form-group col-lg-6 col-md-6">
          <input ref={checkInRef} name="checkIn" type="text" placeholder="Check-In Date" readOnly />
        </div>
        <div className="form-group col-lg-6 col-md-6">
          <input ref={checkOutRef} name="checkOut" type="text" placeholder="Check-Out Date" readOnly />
        </div>
        <div className="form-group col-lg-12">
          <textarea name="message" value={form.message} onChange={handle} placeholder="Your message or special requests…" rows="4"></textarea>
        </div>
        <div className="form-group col-lg-12">
          <button type="submit" className="theme-btn btn-style-one" disabled={loading}>
            <span className="btn-title">{loading ? <><i className="fas fa-spinner fa-spin me-2"></i>Sending…</> : 'Check Availability'}</span>
          </button>
        </div>
      </div>
    </form>
  );
}

/* ─── Home Page ─────────────────────────────────────── */
export default function Home() {
  useEffect(() => {
    const wow = new WOW({ mobile: false, live: true });
    wow.init();
  }, []);

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <BannerSection />
      <BookingForm />

      {/* ── Special Offers / Facilities ── */}
      <section className="offers-section">
        <div className="auto-container">
          <div className="sec-title text-center wow fadeInUp">
            <span className="sub-title">What We Offer</span>
            <h2>World-Class Facilities</h2>
          </div>
          <div className="row">
            {offers.map((o, i) => (
              <div key={i} className="col-lg-4 col-md-6 wow fadeInUp" data-wow-delay={`${i * 150}ms`}>
                <div className="offer-card">
                  <div className="offer-img"><img src={o.img} alt={o.title} /></div>
                  <div className="offer-body">
                    <h5><Link to="/services">{o.title}</Link></h5>
                    <p>{o.desc}</p>
                    <Link to="/services" className="offer-link">Learn More <i className="fas fa-arrow-right"></i></Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section className="home-about-section">
        <div className="auto-container">
          <div className="row align-items-center">
            <div className="col-lg-6 wow fadeInLeft">
              <div className="home-about-images">
                <figure className="img-main"><img src="/images/facilities/restaurant.jpg" alt="Manchester Villa Restaurant" /></figure>
                <figure className="img-inset"><img src="/images/facilities/bar-lounge.jpg" alt="Bar & Lounge" /></figure>
              </div>
            </div>
            <div className="col-lg-6 wow fadeInRight" data-wow-delay="200ms">
              <div className="home-about-content">
                <div className="sec-title">
                  <span className="sub-title">About Manchester Villa Hotel</span>
                  <h2>Comfort, Class &amp; Culture in the Heart of Owerri</h2>
                  <p className="text">Nestled at Plot 34, Area T, behind Apams Casket off PH Road, Owerri, Manchester Villa Hotel is your premium destination for hospitality excellence in Imo State. We blend modern comfort with warm Nigerian hospitality to deliver an unforgettable guest experience.</p>
                </div>
                <div className="about-features">
                  <div className="af-item">
                    <span className="af-icon"><i className="fas fa-utensils"></i></span>
                    <div><strong>In-House Restaurant</strong><span>Nigerian & continental cuisine</span></div>
                  </div>
                  <div className="af-item">
                    <span className="af-icon"><i className="fas fa-martini-glass"></i></span>
                    <div><strong>Premium Bar</strong><span>Stocked with finest spirits</span></div>
                  </div>
                </div>
                <ul className="about-check-list">
                  <li><i className="fas fa-circle-check"></i> 24-hour power supply, security &amp; reception</li>
                  <li><i className="fas fa-circle-check"></i> Free high-speed WiFi in all rooms &amp; common areas</li>
                  <li><i className="fas fa-circle-check"></i> Fully air-conditioned rooms with flat-screen Smart TVs</li>
                </ul>
                <Link to="/about" className="theme-btn btn-style-one mt-3"><span className="btn-title">Discover More</span></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Rooms Swiper ── */}
      <section className="rooms-section">
        <div className="auto-container">
          <div className="sec-title text-center wow fadeInUp">
            <span className="sub-title">Our Accommodation</span>
            <h2>Book Your Stay &amp; Relax in Luxury</h2>
            <p className="text" style={{ maxWidth: 600, margin: '0 auto' }}>
              All rates in Nigerian Naira (₦). Choose from our China (₦30k), Canada (₦35k), UK (₦40k) and USA (₦70k) room tiers — each named to reflect the class and comfort level.
            </p>
          </div>
        </div>
        <div className="rooms-swiper-wrap">
          <Swiper modules={[Autoplay, Navigation]} navigation autoplay={{ delay: 3500, disableOnInteraction: false }} loop spaceBetween={24} slidesPerView={1}
            breakpoints={{ 640: { slidesPerView: 1.5 }, 900: { slidesPerView: 2.2 }, 1200: { slidesPerView: 3 }, 1500: { slidesPerView: 3.5 } }}
            className="rooms-swiper">
            {rooms.map((room) => (
              <SwiperSlide key={room.id}><RoomCard room={room} /></SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="text-center mt-4">
          <Link to="/rooms" className="theme-btn btn-style-one"><span className="btn-title">View All Rooms</span></Link>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <TestimonialSlider />

      {/* ── Menu Section ── */}
      <section className="menu-section">
        <div className="auto-container">
          <div className="sec-title text-center wow fadeInUp">
            <span className="sub-title">Our In-House Restaurant</span>
            <h2>Authentic Nigerian &amp; Continental Cuisine</h2>
          </div>
          <div className="row gx-4">
            {menuItems.map((item, i) => (
              <div key={i} className="col-lg-6 col-md-6 col-sm-12 wow fadeInUp" data-wow-delay={`${(i % 2) * 150}ms`}>
                <div className="menu-item-card">
                  <figure className="menu-img"><img src={item.img} alt={item.title} /></figure>
                  <div className="menu-info">
                    <h6 className="menu-name">{item.title}</h6>
                    <span className="menu-desc">Freshly prepared daily by our in-house chefs</span>
                  </div>
                  <div className="menu-right">
                    <span className="menu-price">{item.price}</span>
                    <span className="menu-badge">{item.badge}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Counter ── */}
      <CounterSection />

      {/* ── Contact Section ── */}
      <section className="home-contact-section">
        <div className="home-contact-bg"><img src="/images/background/bg-contact5.jpg" alt="" /></div>
        <div className="auto-container">
          <div className="row justify-content-end">
            <div className="col-lg-7 col-md-10 col-12">
              <div className="home-contact-box wow fadeInRight">
                <div className="sec-title mb-4">
                  <span className="sub-title">Reservations</span>
                  <h2>Get in Touch</h2>
                  <p className="text">For reservations and enquiries, fill the form below or visit us at Plot 34, Area T, behind Apams Casket off PH Road, Owerri.</p>
                </div>
                <ContactForm />
                <div className="contact-phone-strip">
                  <i className="fas fa-phone-volume"></i>
                  <div>
                    <span>Call for reservations</span>
                    <a href="tel:+2348000000000">+234 (800) 000-0000</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
